/**
 * WhatsApp-Vorlagen (Kategorie UTILITY) für Hinweise einer Agentur an ihre Creator (0159, 0167). Eine Quelle für die Vorschau in
 * der App, den Versand (packages/server/src/whatsapp.ts) und das Anlegen im WhatsApp-Business-Konto der Agentur.
 *
 * Aufbau jeder Vorlage: Text mit Platzhaltern {{1}} … (immer {{1}} = Vorname), Fußzeile „Antworten werden nicht gelesen · STOP
 * zum Abmelden“ und ein Knopf „Im Portal öffnen“, dessen Adresse dynamisch endet (Meta verlangt, dass ein Text nicht mit einem
 * Platzhalter endet; der Link steht deshalb im Knopf statt im Text). Werte dürfen bei Meta weder leer sein noch Zeilenumbrüche
 * oder mehr als vier Leerzeichen enthalten; waParams setzt Ersatzwerte und bereinigt.
 */
import type { MailKey } from "./mail-text.js";

export type WaLang = "de" | "en";
/** Woher ein Platzhalter seinen Wert nimmt (Werte aus mailVariables, wie bei den Mails) */
export type WaSlot = "first_name" | "agency" | "deal" | "request" | "invoice" | "contract" | "event" | "event_date" | "deadline";

export interface WaVorlage { de: string; en: string; slots: WaSlot[] }

export const WA_VORLAGEN: Record<string, WaVorlage> = {
  // 14.10.2026: chat_nachricht hat Meta als Marketing eingestuft (Zustellung bei neuen Konten gesperrt); sachliche Fassung unter
  // neuem Namen (Meta sperrt gelöschte Namen 30 Tage)
  chat_hinweis: {
    de: "Hallo {{1}}, {{2}} hat dir im Creator-Portal geantwortet. Öffne den Chat, um die Nachricht zu lesen.",
    en: "Hi {{1}}, {{2}} replied to you in the creator portal. Open the chat to read the message.",
    slots: ["first_name", "agency"],
  },
  rechnung_anfordern: {
    de: "Hallo {{1}}, {{2}} bittet dich um deine Rechnung für {{3}}. Du kannst sie direkt im Creator-Portal hochladen.",
    en: "Hi {{1}}, {{2}} asks you for your invoice for {{3}}. You can upload it directly in the creator portal.",
    slots: ["first_name", "agency", "request"],
  },
  rechnung_korrektur: {
    de: "Hallo {{1}}, zu deiner Rechnung {{2}} hat {{3}} eine Rückfrage. Details und Korrektur findest du im Creator-Portal.",
    en: "Hi {{1}}, {{3}} has a question about your invoice {{2}}. You will find details and the correction in the creator portal.",
    slots: ["first_name", "invoice", "agency"],
  },
  zahlung_nachfrage: {
    de: "Hallo {{1}}, ist die Zahlung zu {{2}} bei dir eingegangen? Bitte bestätige es kurz im Creator-Portal.",
    en: "Hi {{1}}, has the payment for {{2}} reached you? Please confirm briefly in the creator portal.",
    slots: ["first_name", "invoice"],
  },
  zahlung_bestaetigt: {
    de: "Hallo {{1}}, deine Auszahlung zu {{2}} ist unterwegs. Details findest du im Creator-Portal.",
    en: "Hi {{1}}, your payout for {{2}} is on its way. You will find the details in the creator portal.",
    slots: ["first_name", "invoice"],
  },
  vertrag_unterschreiben: {
    de: "Hallo {{1}}, {{2}} hat dir einen Vertrag zur Unterschrift geschickt ({{3}}). Du kannst ihn im Creator-Portal lesen und unterschreiben.",
    en: "Hi {{1}}, {{2}} sent you a contract to sign ({{3}}). You can read and sign it in the creator portal.",
    slots: ["first_name", "agency", "contract"],
  },
  // 14.10.2026: abgesagte Termine erreichen Creator auch per WhatsApp (Ereignis calendar_event_cancelled, 0259)
  termin_abgesagt: {
    de: "Hallo {{1}}, {{2}} hat einen Termin abgesagt: {{3}} am {{4}}. Details findest du im Creator-Portal.",
    en: "Hi {{1}}, {{2}} cancelled an appointment: {{3}} on {{4}}. You will find the details in the creator portal.",
    slots: ["first_name", "agency", "event", "event_date"],
  },
  neuer_termin: {
    de: "Hallo {{1}}, neuer Termin von {{2}}: {{3}} am {{4}}. Alle Details findest du im Creator-Portal.",
    en: "Hi {{1}}, new appointment from {{2}}: {{3}} on {{4}}. You will find all details in the creator portal.",
    slots: ["first_name", "agency", "event", "event_date"],
  },
  content_frist: {
    de: "Hallo {{1}}, Erinnerung: Die Content-Deadline für {{2}} ist am {{3}}. Alle Details findest du im Creator-Portal.",
    en: "Hi {{1}}, reminder: the content deadline for {{2}} is on {{3}}. You will find all details in the creator portal.",
    slots: ["first_name", "deal", "deadline"],
  },
  beitrag_entwurf_faellig: {
    de: "Hallo {{1}}, der Entwurf für {{2}} ist bis {{3}} fällig. Du kannst ihn im Creator-Portal hochladen.",
    en: "Hi {{1}}, the draft for {{2}} is due by {{3}}. You can upload it in the creator portal.",
    slots: ["first_name", "deal", "deadline"],
  },
  beitrag_veroeffentlichen: {
    de: "Hallo {{1}}, {{2}} soll am {{3}} veröffentlicht werden. Details findest du im Creator-Portal.",
    en: "Hi {{1}}, {{2}} is due to be published on {{3}}. You will find the details in the creator portal.",
    slots: ["first_name", "deal", "deadline"],
  },
  content_korrektur: {
    de: "Hallo {{1}}, zu deinem Content für {{2}} gibt es Änderungswünsche. Alle Kommentare findest du im Creator-Portal.",
    en: "Hi {{1}}, there are change requests for your content for {{2}}. You will find all comments in the creator portal.",
    slots: ["first_name", "deal"],
  },
  beitrag_ueberfaellig: {
    // Meta lehnt Texte ab, die mit einem Platzhalter enden (Befund 06.10.2026): {{3}} deshalb nicht ans Ende
    de: "Hallo {{1}}, der Beitrag für {{2}} ist überfällig. Bei Fragen melde dich bei {{3}}, hochladen kannst du ihn im Creator-Portal.",
    en: "Hi {{1}}, the post for {{2}} is overdue. If you have questions, contact {{3}}; you can upload it in the creator portal.",
    slots: ["first_name", "deal", "agency"],
  },
};

export const WA_FUSS: Record<WaLang, string> = {
  de: "Antworten werden nicht gelesen · STOP zum Abmelden",
  en: "Replies are not read · reply STOP to opt out",
};
export const WA_KNOPF: Record<WaLang, string> = { de: "Im Portal öffnen", en: "Open in portal" };

/** Ersatzwerte, wenn eine Meldung einen Wert nicht mitbringt (Meta lehnt leere Werte ab) */
const ERSATZ: Record<WaSlot, Record<WaLang, string>> = {
  first_name: { de: "zusammen", en: "there" },
  agency: { de: "deiner Agentur", en: "your agency" },
  deal: { de: "deine Kooperation", en: "your collaboration" },
  request: { de: "deine Kooperation", en: "your collaboration" },
  invoice: { de: "aus dem Portal", en: "from the portal" },
  contract: { de: "Vertrag", en: "contract" },
  event: { de: "Termin", en: "Appointment" },
  event_date: { de: "dem genannten Tag", en: "the given day" },
  deadline: { de: "dem genannten Tag", en: "the given day" },
};

/** Wert bereinigen: keine Zeilenumbrüche oder Tabs, höchstens vier Leerzeichen am Stück, höchstens 200 Zeichen */
export function waClean(v: string | null | undefined): string {
  return (v ?? "").replace(/[\r\n\t]+/g, " ").replace(/ {2,}/g, " ").trim().slice(0, 200);
}

/** Werte der Platzhalter in Reihenfolge {{1}}, {{2}} … aus den Mail-Werten einer Meldung (mailVariables). */
export function waParams(template: string, vars: Partial<Record<MailKey, string>>, lang: WaLang): string[] {
  const v = WA_VORLAGEN[template];
  if (!v) throw new Error(`Unbekannte WhatsApp-Vorlage: ${template}`);
  const pick = (s: WaSlot): string => {
    const deal = vars["deal.title"] ?? vars["brand.name"];
    switch (s) {
      case "first_name": return waClean(vars["talent.first_name"]);
      case "agency": return waClean(vars["agency.name"]);
      case "deal": return waClean(deal);
      case "request": return waClean([deal, vars["period.month"]].filter(Boolean).join(", "));
      case "invoice": return waClean(vars["invoice.number"] ?? [vars["brand.name"], vars["period.month"]].filter(Boolean).join(", "));
      case "contract": return waClean(vars["contract.title"]);
      case "event": return waClean(vars["event.title"]);
      case "event_date": return waClean([vars["event.date"], vars["event.time"]].filter(Boolean).join(", "));
      case "deadline": return waClean(vars["deadline.date"]);
    }
  };
  return v.slots.map((s) => pick(s) || ERSATZ[s][lang]);
}

/** Text mit eingesetzten Werten (Vorschau in der App, Probelauf). */
export function waText(template: string, params: readonly string[], lang: WaLang): string {
  const v = WA_VORLAGEN[template];
  if (!v) return "";
  return v[lang].replace(/\{\{(\d+)\}\}/g, (_, n: string) => params[Number(n) - 1] ?? "…");
}

/** Beispielwerte für Vorschau und Meta-Einreichung (erfunden) */
export function waBeispiel(template: string, agency: string, lang: WaLang): string[] {
  const vars: Partial<Record<MailKey, string>> = {
    "talent.first_name": "Mia", "agency.name": agency, "deal.title": "Lunava", "brand.name": "Lunava", "period.month": "September 2026",
    "invoice.number": "R-2026-014", "contract.title": "Lunava", "event.title": lang === "en" ? "Shoot Lunava" : "Dreh Lunava",
    "event.date": "14.10.2026", "deadline.date": "14.10.2026",
  };
  return waParams(template, vars, lang);
}

/**
 * Vorlage, wie sie im WhatsApp-Business-Konto angelegt wird (POST /{waba}/message_templates). appBase: feste Adresse der App,
 * der Knopf hängt den Pfad des Objekts an (z. B. go/invoice/…?als=creator).
 */
export function waMetaTemplate(template: string, lang: WaLang, appBase: string): Record<string, unknown> {
  const v = WA_VORLAGEN[template];
  if (!v) throw new Error(`Unbekannte WhatsApp-Vorlage: ${template}`);
  const base = appBase.replace(/\/+$/, "");
  return {
    name: template,
    language: lang,
    category: "UTILITY",
    components: [
      { type: "BODY", text: v[lang], example: { body_text: [waBeispiel(template, "Nordlicht Talents", lang)] } },
      { type: "FOOTER", text: WA_FUSS[lang] },
      { type: "BUTTONS", buttons: [{ type: "URL", text: WA_KNOPF[lang], url: `${base}/{{1}}`, example: [`${base}/go/deal/beispiel?als=creator`] }] },
    ],
  };
}

/** Nachricht, die beim Versand an die Cloud API geht (POST /{phone_number_id}/messages). linkPath ohne führenden Schrägstrich. */
export function waMessage(to: string, template: string, lang: WaLang, params: readonly string[], linkPath: string): Record<string, unknown> {
  return {
    messaging_product: "whatsapp",
    to: to.replace(/[^0-9]/g, ""),
    type: "template",
    template: {
      name: template,
      language: { code: lang },
      components: [
        { type: "body", parameters: params.map((text) => ({ type: "text", text })) },
        { type: "button", sub_type: "url", index: "0", parameters: [{ type: "text", text: linkPath.replace(/^\/+/, "") || "creator" }] },
      ],
    },
  };
}

/** Automatische Antwort auf eine Nachricht des Creators (höchstens einmal in 24 Stunden; freier Text im Kundenservice-Fenster). */
export function waAutoReply(lang: WaLang, firstName: string | null, agency: string | null, link: string, stop: boolean): string {
  const name = waClean(firstName) || ERSATZ.first_name[lang];
  const ag = waClean(agency) || ERSATZ.agency[lang];
  if (stop) {
    return lang === "en"
      ? `Hi ${name}, you will no longer receive WhatsApp notices from ${ag}. Emails and the portal continue as usual. You can switch WhatsApp back on in your profile: ${link}`
      : `Hallo ${name}, du bekommst von ${ag} keine WhatsApp-Hinweise mehr. Mails und das Portal laufen wie gewohnt weiter. Wieder einschalten kannst du WhatsApp in deinem Profil: ${link}`;
  }
  return lang === "en"
    ? `Hi ${name}, ${ag} only sends notices via this number, nobody reads replies here. Please write in the creator portal: ${link}. Reply STOP and you will no longer receive WhatsApp notices.`
    : `Hallo ${name}, über diese Nummer schickt ${ag} nur Hinweise, Antworten liest hier niemand. Schreib bitte im Creator-Portal: ${link}. Mit STOP bekommst du keine WhatsApp-Hinweise mehr.`;
}

/** STOP-Wörter (Groß/Klein egal, Satzzeichen am Rand egal) */
export function waIsStop(text: string | null | undefined): boolean {
  const t = (text ?? "").trim().toLowerCase().replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");
  return ["stop", "stopp", "abmelden", "unsubscribe", "abbestellen"].includes(t);
}
